import {
  Add as AddIcon,
  Archive as ArchiveIcon,
  AttachFile as AttachFileIcon,
  Edit as EditIcon,
  Notes as NotesIcon,
  Search as SearchIcon,
} from "@mui/icons-material";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  FormControlLabel,
  GridLegacy as Grid,
  IconButton,
  InputAdornment,
  Link,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";

import { AdminDialog, AdminEmptyState, AdminFilterPanel, PageShell } from "@/components/common";
import { LoadingState } from "@/components/common/LoadingState";
import { CustomerNoteEditDialog } from "@/pages/admin/organization/customers/CustomerNoteEditDialog";
import {
  type Customer,
  type CustomerNote,
  CustomerService,
} from "@/services/features/CustomerService";

interface AddNoteFormData {
  title: string;
  content: string;
  fileUrl: string;
}

const getAttachmentFileName = (fileUrl: string): string => {
  try {
    const pathSegments = new URL(fileUrl).pathname.split("/").filter(Boolean);
    const fileName = pathSegments.at(-1);
    return fileName ? decodeURIComponent(fileName) : fileUrl;
  } catch {
    return fileUrl;
  }
};

const CustomerNotes: React.FC = () => {
  const { customerId } = useParams<{ customerId: string }>();
  const { t } = useTranslation();
  const theme = useTheme();
  const customerService = CustomerService.getInstance();

  const [notes, setNotes] = useState<CustomerNote[]>([]);
  const [customer, setCustomer] = useState<Customer | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [dialogError, setDialogError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState({ title: false, content: false });
  const titleInputRef = useRef<HTMLInputElement>(null);
  const contentInputRef = useRef<HTMLInputElement>(null);
  const [showArchived, setShowArchived] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingNote, setEditingNote] = useState<CustomerNote | null>(null);
  const [formData, setFormData] = useState<AddNoteFormData>({
    title: "",
    content: "",
    fileUrl: "",
  });

  const pageTitle = customer
    ? t("management.customers.customerNotes.title", {
        customerName: customer.name,
        customerId,
      })
    : t("management.customers.customerNotes.titleLoading");

  useEffect(() => {
    document.title = customer
      ? t("management.customers.customerNotes.documentTitle", {
          customerName: customer.name,
          customerId,
        })
      : pageTitle;
  }, [customer, customerId, pageTitle, t]);

  useEffect(() => {
    const loadCustomerAndNotes = async () => {
      setCustomer(null);
      await fetchCustomer();
      await fetchNotes();
    };

    void loadCustomerAndNotes();
  }, [customerId]);

  const fetchCustomer = async () => {
    if (!customerId) return;

    const response = await customerService.getCustomerById(parseInt(customerId));
    setCustomer(response.success && response.data ? response.data : null);
  };

  const fetchNotes = async () => {
    if (!customerId) return;

    try {
      setIsLoading(true);
      setError(null);
      const response = await customerService.getAllCustomerNotes(parseInt(customerId));
      if (response.success && response.data) {
        setNotes(sortAlphabetically(response.data));
      } else {
        setError(t("management.customers.customerNotes.errors.failedFetchCustomerNotes"));
      }
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : t("management.customers.customerNotes.errors.errorFetchingCustomerNotes"),
      );
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = async (event?: React.FormEvent) => {
    event?.preventDefault();
    if (!customerId) return;

    setDialogError(null);
    const nextFieldErrors = {
      title: !formData.title.trim(),
      content: !formData.content.trim(),
    };
    setFieldErrors(nextFieldErrors);
    if (nextFieldErrors.title || nextFieldErrors.content) {
      (nextFieldErrors.title ? titleInputRef : contentInputRef).current?.focus();
      return;
    }

    try {
      setIsLoading(true);

      const noteData = {
        customerId: parseInt(customerId),
        ...formData,
        archived: false,
      };

      const response = await customerService.addCustomerNote(noteData);
      if (response.success && response.data) {
        setNotes((prev) => sortAlphabetically([...prev, response.data as CustomerNote]));
        setIsModalOpen(false);
        resetForm();
      } else {
        setDialogError(
          response.error || t("management.customers.customerNotes.errors.failedCreateNote"),
        );
      }
    } catch (err) {
      setDialogError(
        err instanceof Error
          ? err.message
          : t("management.customers.customerNotes.errors.errorCreatingNote"),
      );
    } finally {
      setIsLoading(false);
    }
  };

  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    if (!event.target.files?.[0]) return;

    try {
      setIsLoading(true);
      const formData = new FormData();
      formData.append("file", event.target.files[0]);

      const response = await customerService.uploadFile(formData);
      if (response.success && response.data) {
        setFormData((prev) => {
          const newFormData: AddNoteFormData = {
            ...prev,
            fileUrl: response.data || "",
          };
          return newFormData;
        });
      } else {
        setDialogError(
          response.error || t("management.customers.customerNotes.errors.failedUploadFile"),
        );
      }
    } catch (err) {
      setDialogError(
        err instanceof Error
          ? err.message
          : t("management.customers.customerNotes.errors.errorUploadingFile"),
      );
    } finally {
      setIsLoading(false);
    }
  };

  const sortAlphabetically = (array: CustomerNote[]) => {
    return [...array].sort((a, b) =>
      a.title.localeCompare(b.title, undefined, { numeric: true, sensitivity: "base" }),
    );
  };

  const resetForm = () => {
    setFormData({
      title: "",
      content: "",
      fileUrl: "",
    });
  };

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
    if ((name === "title" || name === "content") && value.trim()) {
      setFieldErrors((current) => ({ ...current, [name]: false }));
    }
  };

  const closeAddDialog = () => {
    setIsModalOpen(false);
    setDialogError(null);
    setFieldErrors({ title: false, content: false });
  };

  const normalizedSearchQuery = searchQuery.trim().toLocaleLowerCase();
  const filteredNotes = notes.filter((note) => {
    if (!showArchived && note.archived) return false;
    if (!normalizedSearchQuery) return true;

    return [note.title, note.content].some((value) =>
      value.toLocaleLowerCase().includes(normalizedSearchQuery),
    );
  });
  const hasSearchQuery = Boolean(normalizedSearchQuery);
  const archivedCardBackground = alpha(
    theme.palette.text.primary,
    theme.palette.mode === "dark" ? 0.08 : 0.035,
  );
  const archivedChipBackground = alpha(
    theme.palette.primary.main,
    theme.palette.mode === "dark" ? 0.24 : 0.1,
  );

  return (
    <PageShell
      breadcrumbs="admin"
      title={pageTitle}
      icon={<NotesIcon />}
      headerActions={
        <Button
          variant="contained"
          color="primary"
          startIcon={<AddIcon />}
          onClick={() => {
            setDialogError(null);
            setFieldErrors({ title: false, content: false });
            setIsModalOpen(true);
          }}
        >
          {t("management.customers.customerNotes.actions.addNote")}
        </Button>
      }
    >
      <Box sx={{ mb: 4 }}>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <AdminFilterPanel sx={{ mb: 3 }}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md>
              <TextField
                fullWidth
                label={t("management.customers.customerNotes.filters.searchLabel")}
                placeholder={t("management.customers.customerNotes.filters.searchPlaceholder")}
                value={searchQuery}
                onChange={(event) => setSearchQuery(event.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={12} md="auto">
              <FormControlLabel
                label={t("management.customers.customerNotes.actions.showArchived")}
                labelPlacement="end"
                control={
                  <Switch
                    checked={showArchived}
                    onChange={(event) => setShowArchived(event.target.checked)}
                  />
                }
                sx={{
                  m: 0,
                  justifyContent: { xs: "space-between", md: "flex-start" },
                  width: "100%",
                }}
              />
            </Grid>
          </Grid>
        </AdminFilterPanel>

        {isLoading && !notes.length ? (
          <LoadingState label={t("common.loading")} />
        ) : (
          <Grid container spacing={3}>
            {filteredNotes.length > 0 ? (
              filteredNotes.map((note) => (
                <Grid item xs={12} key={note.id}>
                  <Card
                    sx={{
                      bgcolor: note.archived ? archivedCardBackground : "background.paper",
                    }}
                  >
                    <CardContent sx={{ p: 3, "&:last-child": { pb: 3 } }}>
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          gap: 1,
                          mb: 3,
                        }}
                      >
                        <Stack
                          direction="row"
                          spacing={1}
                          alignItems="center"
                          sx={{ minWidth: 0, flex: 1, flexWrap: "wrap" }}
                        >
                          <Typography variant="h5" component="h2" sx={{ overflowWrap: "anywhere" }}>
                            {note.title}
                          </Typography>
                          {note.archived && (
                            <Chip
                              size="small"
                              icon={<ArchiveIcon />}
                              label={t("management.customers.customerNotes.status.archived")}
                              variant="outlined"
                              sx={{
                                bgcolor: archivedChipBackground,
                                borderColor: alpha(theme.palette.primary.main, 0.3),
                                color: "primary.main",
                                "& .MuiChip-icon": { color: "primary.main" },
                              }}
                            />
                          )}
                        </Stack>
                        <Tooltip title={t("management.customers.customerNotes.actions.edit")}>
                          <IconButton
                            size="small"
                            color="primary"
                            onClick={() => setEditingNote(note)}
                            aria-label={t("management.customers.customerNotes.actions.edit")}
                          >
                            <EditIcon />
                          </IconButton>
                        </Tooltip>
                      </Box>
                      <Typography sx={{ whiteSpace: "pre-wrap", mb: 3 }}>{note.content}</Typography>
                      {note.fileUrl && (
                        <Link
                          href={note.fileUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          sx={{ display: "inline-flex", alignItems: "center", gap: 0.5 }}
                        >
                          <AttachFileIcon fontSize="small" />
                          <Typography
                            component="span"
                            variant="body2"
                            sx={{ overflowWrap: "anywhere" }}
                          >
                            {getAttachmentFileName(note.fileUrl)}
                          </Typography>
                        </Link>
                      )}
                    </CardContent>
                  </Card>
                </Grid>
              ))
            ) : (
              <Grid item xs={12}>
                <AdminEmptyState
                  icon={<NotesIcon sx={{ fontSize: 40 }} />}
                  text={
                    hasSearchQuery
                      ? t("management.customers.customerNotes.empty.noMatchingNotes")
                      : notes.length
                        ? t("management.customers.customerNotes.empty.noActiveNotes")
                        : t("management.customers.customerNotes.empty.noNotes")
                  }
                />
              </Grid>
            )}
          </Grid>
        )}
      </Box>

      <AdminDialog
        open={isModalOpen}
        onClose={closeAddDialog}
        maxWidth="sm"
        title={t("management.customers.customerNotes.dialogs.addNoteTitle")}
        icon={<NotesIcon />}
        error={dialogError}
        errorOperation="create"
        errorEntity={t("common.entities.note")}
        cancelAction={{ label: t("common.cancel"), onClick: closeAddDialog }}
        primaryAction={{
          label: isLoading
            ? t("management.customers.customerNotes.actions.adding")
            : t("management.customers.customerNotes.actions.add"),
          onClick: () => void handleSubmit(),
          loading: isLoading,
        }}
      >
        <form noValidate onSubmit={handleSubmit}>
          <TextField
            fullWidth
            label={t("management.customers.customerNotes.fields.title")}
            name="title"
            value={formData.title}
            onChange={handleInputChange}
            inputRef={titleInputRef}
            error={fieldErrors.title}
            helperText={fieldErrors.title ? t("common.required") : undefined}
            required
            margin="normal"
          />
          <TextField
            fullWidth
            label={t("management.customers.customerNotes.fields.content")}
            name="content"
            value={formData.content}
            onChange={handleInputChange}
            inputRef={contentInputRef}
            error={fieldErrors.content}
            helperText={fieldErrors.content ? t("common.required") : undefined}
            required
            margin="normal"
            multiline
            rows={5}
          />
          <Box sx={{ mt: 2, mb: 1 }}>
            <input type="file" onChange={handleFileUpload} style={{ marginBottom: "8px" }} />
            <TextField
              fullWidth
              label={t("management.customers.customerNotes.fields.fileUrl")}
              name="fileUrl"
              value={formData.fileUrl}
              onChange={handleInputChange}
              margin="normal"
            />
          </Box>
        </form>
      </AdminDialog>
      <CustomerNoteEditDialog
        note={editingNote}
        onClose={() => setEditingNote(null)}
        onSuccess={(updatedNote) =>
          setNotes((current) =>
            sortAlphabetically(
              current.map((note) => (note.id === updatedNote.id ? updatedNote : note)),
            ),
          )
        }
      />
    </PageShell>
  );
};

export default CustomerNotes;
